import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

function dataAttributesFor(element: RendererProps["element"]) {
  const raw = (element as {
    dataAttributes?: Record<string, string | number | boolean | null | undefined>
  }).dataAttributes
  const attrs: Record<string, string | number | boolean> = {}

  if (!raw) return attrs

  for (const [rawKey, value] of Object.entries(raw)) {
    if (value == null) continue
    const key = rawKey.startsWith("data-")
      ? rawKey
      : `data-${rawKey.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)}`
    attrs[key] = value
  }

  return attrs
}

export function IframeRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const attrs = element as {
    src?: string
    srcDoc?: string
    title?: string
    ariaLabel?: string
    label?: string
    allow?: string
    sandbox?: string
    referrerPolicy?: React.HTMLAttributeReferrerPolicy
    loading?: boolean | string
  }
  const src = attrs.src ? String(resolveSymbol(attrs.src) ?? "") : undefined
  const srcDoc = attrs.srcDoc ? String(resolveSymbol(attrs.srcDoc) ?? "") : undefined
  const label = attrs.label ? String(resolveSymbol(attrs.label) ?? "") : undefined
  const ariaLabel = attrs.ariaLabel
    ? String(resolveSymbol(attrs.ariaLabel) ?? "")
    : label
  const title = attrs.title
    ? String(resolveSymbol(attrs.title) ?? "")
    : ariaLabel ?? "Embedded preview"
  const inlineStyle = element.style as React.CSSProperties | undefined
  const loading =
    attrs.loading === true
      ? "lazy"
      : attrs.loading === "lazy" || attrs.loading === "eager"
        ? attrs.loading
        : undefined

  return (
    <iframe
      src={src}
      srcDoc={srcDoc}
      title={title}
      aria-label={ariaLabel}
      aria-description={label}
      data-label={label}
      allow={attrs.allow}
      sandbox={attrs.sandbox}
      referrerPolicy={attrs.referrerPolicy}
      loading={loading}
      // Iframes default to inline flow with a 2px UA border, so a full-width or
      // wide embed overflows its container horizontally. Default to a block,
      // border-free element capped at the container width; element.className still
      // overrides (twMerge) for explicit sizing.
      className={cn("block w-full max-w-full border-0", element.className)}
      style={inlineStyle}
      data-atom-type="iframe"
      data-variant={element.variant ?? "default"}
      data-size={element.size ?? "default"}
      {...dataAttributesFor(element)}
    />
  )
}
